import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';

// Import components
import { Invoice } from 'components/elements';
import { NavigationButtons, Skeleton } from 'components/views/offer_configurator/components/common';
import { TotalPrice } from './components/TotalPrice/TotalPrice';
import { CurrentProduct } from './components/CurrentProduct';

// Import hooks
import { useBasket } from 'components/views/offer_configurator/hooks';

// Import styles
import { Divider, HeadingWrapper, StyledButton, InvoiceWrapper } from './styles';
import { StepHeadingSecond, StepInfoContent, InfoContentWrapper } from 'components/views/offer_configurator/components/common/styles';

// Import helpers
import { renderSummaryProduct, renderCurrentAdditionalProduct } from './helpers';

// Import types
import { PacketSubtype } from 'types/api/products/product';

type Props = {
	formId?: string;
	disabled?: boolean;
	modifiers?: string;
};

export const Basket = ({ formId, disabled, modifiers }: Props) => {
	const { t } = useTranslation('configurator');
	const [needInvoice, needInvoiceToggle] = useToggle(false);
	const { isLoading, summary, subscription, products, selectedMain, settings, inAdvance, onDetailsButtonClick } = useBasket();

	return (
		<StepInfoContent modifiers={modifiers}>
			<HeadingWrapper>
				<StepHeadingSecond>{t('summary_basket_heading')}</StepHeadingSecond>

				{!isLoading && (
					<StyledButton onClick={onDetailsButtonClick} variant="primary" modifiers={['text', 'small']}>
						{t('button_details')}
					</StyledButton>
				)}
			</HeadingWrapper>

			<InfoContentWrapper>
				<Skeleton isLoading={isLoading} count={summary ? summary.products.length + 2 : 2}>
					{/* current main packet */}
					{subscription && (
						<CurrentProduct
							subtype={PacketSubtype.Main}
							product={subscription.main}
							isRemoved={selectedMain?.uuid !== subscription.main.product_uuid}
						/>
					)}

					{/* new main packet */}
					{products.main?.map(renderSummaryProduct)}

					{/* current additional products */}
					{subscription?.premium.map(renderCurrentAdditionalProduct)}

					{/* new additional products */}
					{products.premium?.map(renderSummaryProduct)}

					{summary && (
						<>
							<Divider />
							<TotalPrice duration={settings?.billing_duration.period} summary={summary} inAdvance={inAdvance} />
						</>
					)}
				</Skeleton>
			</InfoContentWrapper>

			{summary && (
				<NavigationButtons disabled={disabled || isLoading} formId={formId}>
					<InvoiceWrapper>
						<Invoice needInvoice={needInvoice} needInvoiceToggle={needInvoiceToggle} />
					</InvoiceWrapper>
				</NavigationButtons>
			)}
		</StepInfoContent>
	);
};
